// ---------------------------------------------------------------------------
// Column Factory
// ---------------------------------------------------------------------------
// Generates TanStack Table ColumnDef from EnhancedColumnConfig metadata.
// Single source of truth: column definitions in constants.ts drive rendering.
// ---------------------------------------------------------------------------

import React from 'react';
import type { ColumnDef, CellContext } from '@tanstack/react-table';
import type {
  EnhancedColumnConfig,
  PlatformId,
  DataSourceId,
} from '@/components/v11/discovery/types';
import type { TableMeta } from '@/components/v11/table/types';
import { getDrillDownRelation } from '@/components/v11/discovery/drillDownConfig';
import { Badge } from '@/components/ui/badge';
import {
  PlatformBadge,
  MediaTypeBadge,
  EmotionBadge,
  TeamTypeBadge,
  SentimentCell,
  NumberCell,
  CompactNumberCell,
  TruncatedText,
  TextCell,
  ExternalProfileLink,
  ExternalPostLink,
} from '@/lib/cellRenderers';
import { buildPostUrl } from '@/lib/socialUrlBuilder';

// ---------------------------------------------------------------------------
// Cell Renderer Registry
// ---------------------------------------------------------------------------

/**
 * Cell renderer function type
 */
type CellRendererFn = (value: unknown, options?: Record<string, unknown>) => React.ReactNode;

/**
 * Maps cell renderer types to render functions
 * Add new renderers here when extending CellRendererType
 */
const CELL_RENDERER_MAP: Record<string, CellRendererFn> = {
  text: (value: unknown) =>
    React.createElement(TextCell, { value: value as string | null | undefined }),

  mutedId: (value: unknown) => {
    if (value == null)
      return React.createElement('span', { className: 'text-v11-text-muted' }, '—');
    return React.createElement(
      'span',
      { className: 'text-[11px] font-mono text-v11-text-muted/60 tabular-nums tracking-tight' },
      `#${value}`
    );
  },

  number: (value: unknown) =>
    React.createElement(NumberCell, { value: value as number | null | undefined }),

  compactNumber: (value: unknown) =>
    React.createElement(CompactNumberCell, { value: value as number | null | undefined }),

  platform: (value: unknown) =>
    React.createElement(PlatformBadge, { platform: value as PlatformId }),

  emotion: (value: unknown) =>
    React.createElement(EmotionBadge, { emotion: value as string | null }),

  mediaType: (value: unknown) =>
    React.createElement(MediaTypeBadge, { mediaType: value as string | null }),

  teamType: (value: unknown) =>
    React.createElement(TeamTypeBadge, { teamType: value as string | null }),

  sentiment: (value: unknown) =>
    React.createElement(SentimentCell, { value: value as number | null | undefined }),

  truncatedText: (value: unknown, options?: Record<string, unknown>) =>
    React.createElement(TruncatedText, {
      text: value as string | null,
      maxLength: options?.maxLength as number | undefined,
    }),

  date: (value: unknown) => {
    const date = value as string | null;
    return date ? new Date(date).toLocaleDateString() : '—';
  },

  boolean: (value: unknown) =>
    React.createElement('span', { className: 'text-xs' }, value ? '✓' : ''),

  booleanEmoji: (value: unknown, options?: Record<string, unknown>) => {
    const emoji = options?.emoji as string | undefined;
    return React.createElement('span', { className: 'text-xs' }, value ? emoji || '✓' : '—');
  },

  avatar: (value: unknown) => {
    const src = value as string | null | undefined;
    return React.createElement(
      'div',
      {
        className: src
          ? 'h-8 w-8 rounded-full bg-v11-surface-hover overflow-hidden flex items-center justify-center'
          : 'h-8 w-8 rounded-full bg-v11-surface-hover overflow-hidden flex items-center justify-center border border-v11-border',
      },
      src
        ? React.createElement('img', {
            src,
            alt: '',
            className: 'h-full w-full object-cover',
            loading: 'lazy',
          })
        : React.createElement(
            'svg',
            {
              xmlns: 'http://www.w3.org/2000/svg',
              width: 14,
              height: 14,
              viewBox: '0 0 24 24',
              fill: 'none',
              stroke: 'currentColor',
              strokeWidth: 1.5,
              strokeLinecap: 'round',
              strokeLinejoin: 'round',
              className: 'text-zinc-500',
            },
            React.createElement('rect', { width: 18, height: 18, x: 3, y: 3, rx: 2, ry: 2 }),
            React.createElement('circle', { cx: 9, cy: 9, r: 2 }),
            React.createElement('path', { d: 'm21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21' })
          )
    );
  },

  thumbnail: (value: unknown) => {
    const src = value as string | null | undefined;
    return React.createElement(
      'div',
      {
        className: src
          ? 'h-10 w-14 rounded bg-v11-surface-hover overflow-hidden flex items-center justify-center'
          : 'h-10 w-14 rounded bg-v11-surface-hover overflow-hidden flex items-center justify-center border border-v11-border',
      },
      src
        ? React.createElement('img', {
            src,
            alt: '',
            className: 'h-full w-full object-cover',
            loading: 'lazy',
          })
        : React.createElement(
            'svg',
            {
              xmlns: 'http://www.w3.org/2000/svg',
              width: 16,
              height: 16,
              viewBox: '0 0 24 24',
              fill: 'none',
              stroke: 'currentColor',
              strokeWidth: 1.5,
              strokeLinecap: 'round',
              strokeLinejoin: 'round',
              className: 'text-zinc-500',
            },
            React.createElement('rect', { width: 18, height: 18, x: 3, y: 3, rx: 2, ry: 2 }),
            React.createElement('circle', { cx: 9, cy: 9, r: 2 }),
            React.createElement('path', { d: 'm21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21' })
          )
    );
  },

  sentimentText: (value: unknown) => {
    const val = value as string;
    const color =
      val === 'Positive'
        ? 'text-green-400'
        : val === 'Negative'
          ? 'text-red-400'
          : 'text-yellow-400';
    return React.createElement('span', { className: `font-medium ${color}` }, val);
  },

  performance: (value: unknown) => {
    const val = value as string;
    const label = val?.replace('_performing', '') || 'low';
    const color =
      val === 'high_performing'
        ? 'text-green-400'
        : val === 'medium_performing'
          ? 'text-yellow-400'
          : 'text-gray-400';
    return React.createElement('span', { className: `font-medium ${color} capitalize` }, label);
  },

  booleanBadge: (value: unknown) => {
    const isTrue = value as boolean;
    return isTrue
      ? React.createElement(Badge, { variant: 'destructive', className: 'text-xs' }, 'Yes')
      : React.createElement('span', { className: 'text-v11-text-muted text-xs' }, 'No');
  },

  engagement: (value: unknown) => {
    const val = value as number | undefined;
    if (val == null) return React.createElement('span', { className: 'text-v11-text-muted' }, '—');
    return React.createElement(
      'span',
      { className: 'text-v11-text-secondary font-medium tabular-nums' },
      `${val.toFixed(1)}%`
    );
  },

  platforms: (value: unknown) => {
    const platforms = value as readonly PlatformId[] | undefined;
    if (!platforms || platforms.length === 0) {
      return React.createElement('span', { className: 'text-v11-text-muted' }, '—');
    }
    return React.createElement(
      'div',
      { className: 'flex gap-1' },
      platforms.map(p => React.createElement(PlatformBadge, { key: p, platform: p }))
    );
  },

  badge: (value: unknown) =>
    React.createElement(Badge, { variant: 'outline', className: 'text-xs' }, value as string),

  categoryBadge: (value: unknown) => {
    const category = value as string;
    const colorMap: Record<string, string> = {
      Main: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
      Influencer: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
      Partner: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
      Competitor: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
    };
    const colors = colorMap[category] ?? 'bg-gray-500/15 text-gray-400 border-gray-500/30';
    return React.createElement(
      'span',
      {
        className: `inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${colors}`,
      },
      category
    );
  },

  analysisStatus: (value: unknown) => {
    const status = value as number | null;
    const statusMap: Record<number, { label: string; colors: string }> = {
      0: { label: 'Pending', colors: 'bg-gray-500/15 text-gray-400 border-gray-500/30' },
      1: { label: 'Processing', colors: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30' },
      2: { label: 'Complete', colors: 'bg-green-500/15 text-green-400 border-green-500/30' },
      3: { label: 'Failed', colors: 'bg-red-500/15 text-red-400 border-red-500/30' },
    };
    const info = status != null ? statusMap[status] : null;
    if (!info)
      return React.createElement('span', { className: 'text-v11-text-muted text-xs' }, '—');
    return React.createElement(
      'span',
      {
        className: `inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${info.colors}`,
      },
      info.label
    );
  },

  toggle: (value: unknown) => {
    const enabled = value as boolean;
    return React.createElement(
      'div',
      { className: 'flex items-center justify-center' },
      React.createElement(
        'div',
        {
          className: `relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
            enabled ? 'bg-green-500' : 'bg-gray-600'
          }`,
        },
        React.createElement('span', {
          className: `inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${
            enabled ? 'translate-x-4' : 'translate-x-0.5'
          }`,
        })
      )
    );
  },
};

// ---------------------------------------------------------------------------
// Factory Function
// ---------------------------------------------------------------------------

/**
 * Builds TanStack Table ColumnDef array from EnhancedColumnConfig metadata
 *
 * @template T - Row type for the table
 * @param configs - Array of enhanced column configurations
 * @returns Array of ColumnDef objects for TanStack Table
 *
 * @example
 * ```typescript
 * const columns = buildColumnsForSource<ProfileRow>(COLUMNS_PROFILES)
 * ```
 *
 * Features:
 * - Maps cellRenderer type to React components
 * - Respects sortable config for enableSorting
 * - Maps width to size for column sizing
 * - Immutable: returns new array each call
 * - Development validation for missing renderers
 */
export function buildColumnsForSource<T = unknown>(
  configs: ReadonlyArray<EnhancedColumnConfig>
): ColumnDef<T>[] {
  return configs.map(config => {
    // Base column definition
    const columnDef: ColumnDef<T> = {
      accessorKey: config.key as keyof T & string,
      header: config.label,
      enableSorting: config.sortable,
      meta: { align: config.align },
    };

    // Add size if width is specified
    if (config.width) {
      // Convert width string (e.g., "120px") to number for TanStack Table
      const widthMatch = config.width.match(/(\d+)/);
      if (widthMatch) {
        columnDef.size = parseInt(widthMatch[1], 10);
        columnDef.minSize = columnDef.size;
      }
    }

    // Add cell renderer if specified
    if (config.cellRenderer) {
      // Special case: interactiveToggle needs access to table.options.meta
      if (config.cellRenderer === 'interactiveToggle') {
        columnDef.cell = (info: CellContext<T, unknown>) => {
          const enabled = info.getValue() as boolean;
          const meta = info.table.options.meta as TableMeta | undefined;
          const row = info.row.original as { datasourceItemId?: number };
          const datasourceItemId = row?.datasourceItemId;

          const handleClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            if (meta?.onToggleScraping && datasourceItemId != null) {
              meta.onToggleScraping(datasourceItemId);
            }
          };

          return React.createElement(
            'div',
            { className: 'flex items-center justify-center' },
            React.createElement(
              'button',
              {
                type: 'button',
                onClick: handleClick,
                className: `relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer hover:opacity-80 ${
                  enabled ? 'bg-green-500' : 'bg-gray-600'
                }`,
                'aria-label': enabled ? 'Disable scraping' : 'Enable scraping',
              },
              React.createElement('span', {
                className: `inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${
                  enabled ? 'translate-x-4' : 'translate-x-0.5'
                }`,
              })
            )
          );
        };
      } else if (config.cellRenderer === 'drillDownNumber') {
        // Special case: drillDownNumber - clickable number that triggers drill-down navigation
        columnDef.cell = (info: CellContext<T, unknown>) => {
          const value = info.getValue() as number | null | undefined;
          const meta = info.table.options.meta as TableMeta | undefined;
          const currentDataSource = meta?.currentDataSource;
          const columnKey = config.key;

          // Get drill-down relation config
          const relation = currentDataSource
            ? getDrillDownRelation(currentDataSource, columnKey)
            : undefined;

          // Get values from row for drill-down params
          const row = info.row.original as Record<string, unknown>;
          const filterValue = relation ? row[relation.valueKey] : undefined;
          const displayLabel = relation ? row[relation.displayKey] : undefined;

          const canDrillDown = Boolean(
            meta?.onDrillDown &&
            relation &&
            filterValue != null &&
            displayLabel != null &&
            value != null &&
            value > 0
          );

          const handleClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            if (canDrillDown && relation && meta?.onDrillDown) {
              meta.onDrillDown({
                targetDataSource: relation.target,
                filterKey: relation.apiParam,
                filterValue: filterValue as string | number,
                displayLabel: String(displayLabel),
              });
            }
          };

          // Format number
          const formattedValue = value != null ? value.toLocaleString() : '—';

          // If drill-down available, render as clickable link
          if (canDrillDown) {
            return React.createElement(
              'button',
              {
                type: 'button',
                onClick: handleClick,
                className:
                  'text-v11-accent hover:text-v11-accent/80 hover:underline font-medium tabular-nums cursor-pointer',
                'aria-label': `View ${relation?.target} for ${displayLabel}`,
              },
              formattedValue
            );
          }

          // Otherwise render as plain number
          return React.createElement(
            'span',
            { className: 'text-v11-text-secondary tabular-nums' },
            formattedValue
          );
        };
      } else if (config.cellRenderer === 'commentsWithAnalyzed') {
        // Special case: commentsWithAnalyzed - shows analyzed(total), with drill-down on analyzed
        // Backend fields:
        //   comments = total real comments by author
        //   commentsAnalyzed = analyzed comments (sentiment_accuracy > 0.49)
        // Comments endpoint drill-down returns only analyzed, so the clickable value = commentsAnalyzed
        columnDef.cell = (info: CellContext<T, unknown>) => {
          const commentsTotal = info.getValue() as number | null | undefined;
          const row = info.row.original as Record<string, unknown>;
          const analyzed = row.commentsAnalyzed as number | null | undefined;
          // Primary = analyzed (matches what drill-down returns)
          const comments = analyzed ?? commentsTotal;
          const meta = info.table.options.meta as TableMeta | undefined;
          const currentDataSource = meta?.currentDataSource;
          const columnKey = config.key;

          // Get drill-down relation config
          const relation = currentDataSource
            ? getDrillDownRelation(currentDataSource, columnKey)
            : undefined;

          // Get values from row for drill-down params
          const filterValue = relation ? row[relation.valueKey] : undefined;
          const displayLabel = relation ? (row[relation.displayKey] ?? relation.labelPrefix) : undefined;

          const canDrillDown = Boolean(
            meta?.onDrillDown &&
            relation &&
            filterValue != null &&
            displayLabel != null &&
            comments != null &&
            comments > 0
          );

          const handleClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            if (canDrillDown && relation && meta?.onDrillDown) {
              meta.onDrillDown({
                targetDataSource: relation.target,
                filterKey: relation.apiParam,
                filterValue: filterValue as string | number,
                displayLabel: String(displayLabel),
              });
            }
          };

          // Format numbers
          const commentsFormatted = comments != null ? comments.toLocaleString() : '—';
          // Show total in parentheses when both values are present
          const totalFormatted =
            analyzed != null && commentsTotal != null ? commentsTotal.toLocaleString() : null;

          // Build the total suffix element (in parentheses)
          const totalSuffix =
            totalFormatted != null
              ? React.createElement(
                  'span',
                  { key: 'analyzed', className: 'text-v11-text-muted ml-1' },
                  `(${totalFormatted})`
                )
              : null;

          // If drill-down available, render analyzed as clickable link
          if (canDrillDown) {
            return React.createElement('span', { className: 'tabular-nums' }, [
              React.createElement(
                'button',
                {
                  key: 'link',
                  type: 'button',
                  onClick: handleClick,
                  className:
                    'text-v11-accent hover:text-v11-accent/80 hover:underline font-medium cursor-pointer',
                  'aria-label': `View ${relation?.target} for ${displayLabel}`,
                },
                commentsFormatted
              ),
              totalSuffix,
            ]);
          }

          // Otherwise render as plain number
          return React.createElement(
            'span',
            { className: 'text-v11-text-secondary tabular-nums' },
            [commentsFormatted, totalSuffix]
          );
        };
      } else if (config.cellRenderer === 'externalProfileLink') {
        // Special case: externalProfileLink - clickable username that opens profile in new tab
        columnDef.cell = (info: CellContext<T, unknown>) => {
          const value = info.getValue() as string | null | undefined;
          const row = info.row.original as Record<string, unknown>;
          const platform = row.platform as string | undefined;

          return React.createElement(ExternalProfileLink, {
            username: value,
            platform: platform,
          });
        };
      } else if (config.cellRenderer === 'externalPostLink') {
        // Special case: externalPostLink - clickable caption that opens post in new tab
        columnDef.cell = (info: CellContext<T, unknown>) => {
          const value = info.getValue() as string | null | undefined;
          const row = info.row.original as Record<string, unknown>;
          // For Instagram, use shortcode or post_shortcode; for other platforms, use id
          const shortcode = (row.shortcode || row.post_shortcode) as string | undefined;
          const id = shortcode || (row.id as string | undefined);
          const author = row.author as string | undefined;
          const platform = row.platform as string | undefined;
          const maxLength = config.cellOptions?.maxLength as number | undefined;

          return React.createElement(ExternalPostLink, {
            caption: value,
            id: id,
            author: author,
            platform: platform,
            maxLength: maxLength,
          });
        };
      } else if (config.cellRenderer === 'thumbnail') {
        // Special case: thumbnail - clickable image that opens post in new tab
        columnDef.cell = (info: CellContext<T, unknown>) => {
          const src = info.getValue() as string | null | undefined;
          const row = info.row.original as Record<string, unknown>;
          const shortcode = (row.shortcode || row.post_shortcode) as string | undefined;
          const id = shortcode || (row.id as string | undefined);
          const author = row.author as string | undefined;
          const platform = row.platform as string | undefined;
          const postUrl = platform && id ? buildPostUrl(platform, id, author) : null;

          const imgElement = src
            ? React.createElement('img', {
                src,
                alt: '',
                className: 'h-full w-full object-cover',
                loading: 'lazy',
              })
            : React.createElement(
                'svg',
                {
                  xmlns: 'http://www.w3.org/2000/svg',
                  width: 16,
                  height: 16,
                  viewBox: '0 0 24 24',
                  fill: 'none',
                  stroke: 'currentColor',
                  strokeWidth: 1.5,
                  strokeLinecap: 'round',
                  strokeLinejoin: 'round',
                  className: 'text-zinc-500',
                },
                React.createElement('rect', { width: 18, height: 18, x: 3, y: 3, rx: 2, ry: 2 }),
                React.createElement('circle', { cx: 9, cy: 9, r: 2 }),
                React.createElement('path', { d: 'm21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21' })
              );

          const containerClass = src
            ? 'h-10 w-14 rounded bg-v11-surface-hover overflow-hidden flex items-center justify-center'
            : 'h-10 w-14 rounded bg-v11-surface-hover overflow-hidden flex items-center justify-center border border-v11-border';

          const container = React.createElement('div', { className: containerClass }, imgElement);

          if (postUrl) {
            return React.createElement(
              'a',
              {
                href: postUrl,
                target: '_blank',
                rel: 'noopener noreferrer',
                className: 'cursor-pointer hover:opacity-80 transition-opacity',
                title: 'Open post',
                onClick: (e: React.MouseEvent) => e.stopPropagation(),
              },
              container
            );
          }

          return container;
        };
      } else {
        const renderer = CELL_RENDERER_MAP[config.cellRenderer];

        if (!renderer) {
          // Development-only warning for missing renderers
          if (process.env.NODE_ENV === 'development') {
            console.warn(
              `[columnFactory] No renderer found for type '${config.cellRenderer}' on column '${config.key}'`
            );
          }
          // Fallback to text renderer
          columnDef.cell = ({ getValue }) =>
            React.createElement(TextCell, { value: getValue() as string });
        } else {
          // Use configured renderer
          columnDef.cell = ({ getValue }) => {
            const value = getValue();
            return renderer(value, config.cellOptions);
          };
        }
      }
    } else {
      // No renderer specified - use default text renderer
      columnDef.cell = ({ getValue }) => {
        const value = getValue();
        return React.createElement(
          'span',
          { className: 'text-v11-text-secondary' },
          String(value ?? '—')
        );
      };
    }

    return columnDef;
  });
}

// ---------------------------------------------------------------------------
// Validation Utilities (Development Only)
// ---------------------------------------------------------------------------

/**
 * Validates that all column keys exist in the row type
 * Only runs in development mode
 *
 * @param configs - Column configurations to validate
 * @param sampleRow - Sample row object to check keys against
 */
export function validateColumnKeys<T = unknown>(
  configs: ReadonlyArray<EnhancedColumnConfig>,
  sampleRow: T
): void {
  if (process.env.NODE_ENV !== 'development') return;

  const rowKeys = new Set(Object.keys(sampleRow as Record<string, unknown>));
  const missingKeys: string[] = [];

  configs.forEach(config => {
    if (!rowKeys.has(config.key)) {
      missingKeys.push(config.key);
    }
  });

  if (missingKeys.length > 0) {
    console.warn(
      '[columnFactory] The following column keys do not exist in the row type:',
      missingKeys
    );
  }
}
